import { useEffect, useMemo, useState } from 'react'
import { Apple, ArrowRight, Download, Loader2, Monitor } from 'lucide-react'
import api from '../../utils/api'

function normalizeTencentId(value) {
  const raw = String(value || '').trim()
  const safeValue = raw.replace(/[^A-Za-z0-9_-]/g, '')
  if (safeValue.length < 4 || safeValue.length > 64) return ''
  return safeValue
}

function AdTencentDownloadButtons({ theme = 'rose', tencentId = '', callbackUrl = '', clickId = '', pageNo = 0 }) {
  const [downloads, setDownloads] = useState([])
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState(null)

  useEffect(() => {
    let mounted = true

    const fetchDownloads = async () => {
      try {
        const response = await api.get('/downloads')
        if (mounted && response.data.success) {
          setDownloads(response.data.downloads || [])
        }
      } catch (error) {
        console.error('获取下载列表失败:', error)
      } finally {
        if (mounted) setLoading(false)
      }
    }

    fetchDownloads()
    return () => {
      mounted = false
    }
  }, [])

  const palette = useMemo(() => {
    const map = {
      rose: {
        card: 'border-rose-100 bg-white hover:border-rose-300 hover:shadow-[0_24px_60px_-30px_rgba(244,63,94,0.28)]',
        icon: 'bg-rose-50 text-rose-600',
        button: 'border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100'
      },
      cyan: {
        card: 'border-cyan-100 bg-white hover:border-cyan-300 hover:shadow-[0_24px_60px_-30px_rgba(34,211,238,0.45)]',
        icon: 'bg-cyan-50 text-cyan-600',
        button: 'border-cyan-200 bg-cyan-50 text-cyan-700 hover:bg-cyan-100'
      }
    }
    return map[theme] || map.rose
  }, [theme])

  const safeTencentId = normalizeTencentId(tencentId)

  useEffect(() => {
    if (tencentId && !safeTencentId) {
      console.warn('检测到无效的 tencentId，已按普通下载处理')
    }
  }, [tencentId, safeTencentId])

  const items = [
    { id: 'windows', label: 'Windows 版本', helper: '适合 Windows 10/11 用户', icon: Monitor },
    { id: 'macos-intel', label: 'Mac Intel 版本', helper: '适合 Intel 芯片 Mac', icon: Apple },
    { id: 'macos-apple', label: 'Mac Apple 版本', helper: '适合 Apple Silicon 芯片 Mac', icon: Apple }
  ].map((item) => {
    const match = downloads.find((download) => download.id === item.id)
    return {
      ...item,
      version: match?.version,
      available: !!match?.file_path
    }
  })

  const handleDownload = (id) => {
    setDownloading(id)
    const params = new URLSearchParams()
    if (callbackUrl) params.set('cb', callbackUrl)
    if (clickId) params.set('clickId', clickId)
    if (safeTencentId) params.set('tencentId', safeTencentId)
    if (pageNo > 0) params.set('pageNo', String(pageNo))
    const query = params.toString() ? `?${params.toString()}` : ''
    const finalUrl = `/api/adtencent/download/${encodeURIComponent(id)}${query}`
    window.location.href = finalUrl
    window.setTimeout(() => setDownloading(null), 1200)
  }

  return (
    <div className="grid gap-4 md:grid-cols-3">
        {loading ? (
          <div className="col-span-full flex items-center justify-center rounded-3xl border border-slate-200 bg-white px-6 py-12 text-slate-600 shadow-sm">
            <Loader2 className="mr-3 h-6 w-6 animate-spin" />
            正在获取下载信息...
          </div>
        ) : (
          items.map((item) => {
            const Icon = item.icon
            const isBusy = downloading === item.id
            const disabled = isBusy || !item.available
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleDownload(item.id)}
                disabled={disabled}
                className={`group rounded-3xl border p-6 text-left text-slate-900 transition duration-300 ${palette.card} ${
                  disabled ? 'cursor-not-allowed opacity-60' : ''
                }`}
              >
                <div className={`mb-5 inline-flex rounded-2xl p-3 ${palette.icon}`}>
                  <Icon className="h-6 w-6" />
                </div>
                <div className="mb-2 text-lg font-semibold">{item.label}</div>
                <div className="mb-5 text-sm leading-6 text-slate-500">
                  {item.available ? item.helper : `${item.helper} · 暂无可下载文件`}
                </div>
                <div className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm ${palette.button}`}>
                  {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  立即下载
                  {!isBusy && <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />}
                </div>
              </button>
            )
          })
        )}
    </div>
  )
}

export default AdTencentDownloadButtons
